import React from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {RiSendPlaneFill, RiCloseCircleLine, RiFileCopyLine, RiKey2Line, RiCheckLine, RiErrorWarningLine} from "react-icons/ri";
import {BsAwardFill} from "react-icons/bs";
import Toggle from "../Toggle";
import ConfirmDialog from "../ConfirmDialog";
import {initials, avatarColorFor} from "../../helpers/avatar";
import {regenerateOperatorKey} from "../../../Services/operatorFunctions";
import {
    Wrap, Empty, Card, HeaderCard, Avatar, HInfo, Name, Sub, LinkRow, IconBtn, HActions, MsgBtn, RemoveBtn, Badge,
    KeyBtn, KeyCard, KeyLabel, KeyValueRow, KeyValue, CopyBtn, KeyNote,
    CardHead, CardTitle, Hint, PresetBtn, RoleGrid, RoleCard, RoleName, RoleDesc, Group, GroupTitle, Row, RowInfo, RowName, OwnerOnly,
    SaveBar, SaveNote, SaveActions, CancelChangesBtn, SaveChangesBtn,
} from "./styles";

export const PRESETS = {
    owner: {can_send_messages: true, can_edit_group: true, can_add_members: true, can_remove_members: true, can_invite_operators: true, can_disconnect_number: true},
    admin: {can_send_messages: true, can_edit_group: true, can_add_members: true, can_remove_members: true, can_invite_operators: true, can_disconnect_number: false},
    operator: {can_send_messages: true, can_edit_group: false, can_add_members: false, can_remove_members: false, can_invite_operators: false, can_disconnect_number: false},
    viewer: {can_send_messages: false, can_edit_group: false, can_add_members: false, can_remove_members: false, can_invite_operators: false, can_disconnect_number: false},
};
const ROLE_CARDS = ["owner", "admin", "operator", "viewer"];
const PERM_KEYS = Object.keys(PRESETS.owner);
// Rascunho inicial a partir do operador (cópia rasa das permissões pra não mutar o prop).
const seedDraft = (op) => (op ? {role: op.role, permissions: {...(op.permissions || {})}} : {role: undefined, permissions: {}});
const MATRIX = [
    {group: "group_conversation", perms: [["can_send_messages", "perm_send_messages"], ["can_edit_group", "perm_edit_group"]]},
    {group: "group_members", perms: [["can_add_members", "perm_add_members"], ["can_remove_members", "perm_remove_members"]]},
    {group: "group_campaign_wide", perms: [["can_invite_operators", "perm_invite_operators"], ["can_disconnect_number", "perm_disconnect"]]},
];

const activity = (op, t) => {
    if (op.status === "pending") return t("words.activity_pending");
    if (!op.last_active_at) return "";
    const m = Math.floor((Date.now() - new Date(op.last_active_at).getTime()) / 60000);
    if (m < 1) return t("words.activity_now");
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
};

const OperatorDetail = ({operator, campaign, onUpdate, onRevoke, onDirtyChange}) => {
    const {t} = useTranslation();
    const {id: campaignId} = useParams();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [genConfirmOpen, setGenConfirmOpen] = React.useState(false);
    const [genKey, setGenKey] = React.useState(null);
    const [genLoading, setGenLoading] = React.useState(false);
    const [genErr, setGenErr] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    // Rascunho local de {role, permissions}: edições ficam aqui e só persistem no Salvar.
    // Re-semeia ao trocar de operador — descarta qualquer edição não salva do anterior.
    const [draft, setDraft] = React.useState(() => seedDraft(operator));
    React.useEffect(() => { setDraft(seedDraft(operator)); }, [operator && operator.id]);

    // Some a chave revelada ao trocar de operador (não vaza de um pro outro).
    React.useEffect(() => { setGenKey(null); setGenErr(false); setCopied(false); }, [operator && operator.id]);

    const owner = !!(operator && operator.synthetic);
    const savedPerms = (operator && operator.permissions) || {};
    const dirty = !!operator && !owner && (
        draft.role !== operator.role ||
        PERM_KEYS.some((k) => !!draft.permissions[k] !== !!savedPerms[k])
    );
    // Reporta o "dirty" pro pai (guarda ao trocar de operador / sair da aba).
    React.useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

    if (!operator) return <Empty>{t("words.select_operator")}</Empty>;
    const perms = draft.permissions || {};

    const generateKey = async () => {
        setGenLoading(true);
        setGenErr(false);
        setCopied(false);
        try {
            const data = await regenerateOperatorKey(campaignId, operator.id);
            setGenKey(data.access_key);
        } catch (e) {
            setGenErr(true);
        }
        setGenLoading(false);
    };
    const copyKey = () => {
        if (genKey && navigator.clipboard) navigator.clipboard.writeText(genKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const pickRole = (role) => {
        if (owner || role === "owner" || role === draft.role) return;
        setDraft({role, permissions: {...PRESETS[role]}});
    };
    const toggle = (key) => {
        if (owner || key === "can_disconnect_number") return;
        setDraft((d) => ({...d, permissions: {...d.permissions, [key]: !d.permissions[key], can_disconnect_number: false}}));
    };
    const resetPreset = () => {
        if (owner) return;
        setDraft((d) => ({...d, permissions: {...PRESETS[d.role]}}));
    };
    const onSaveChanges = () => { if (dirty) onUpdate({role: draft.role, permissions: draft.permissions}); };
    const onCancelChanges = () => setDraft(seedDraft(operator));
    const copyLink = () => {
        if (operator.access_link && navigator.clipboard) navigator.clipboard.writeText(operator.access_link);
    };

    return (
        <Wrap>
            <HeaderCard>
                <Avatar $color={owner ? "#F59E0B" : avatarColorFor(operator.email || operator.id)}>{initials(operator.full_name)}</Avatar>
                <HInfo>
                    <Name>{operator.full_name} <Badge $role={operator.role}>{t(`words.role_${operator.role}`)}</Badge></Name>
                    <Sub>{operator.email ? `${operator.email} · ` : ""}{activity(operator, t)}</Sub>
                    {campaign?.name ? <Sub>{t("words.campaign")}: <b>{campaign.name}</b></Sub> : null}
                    {operator.access_link ? (
                        <LinkRow>
                            <a href={operator.access_link} target="_blank" rel="noopener noreferrer">{operator.access_link}</a>
                            <IconBtn type="button" onClick={copyLink} aria-label={t("words.copy")}><RiFileCopyLine/></IconBtn>
                        </LinkRow>
                    ) : null}
                    {!owner && operator.status !== "revoked" ? (
                        genKey ? (
                            <KeyCard>
                                <KeyValueRow>
                                    <KeyLabel>{t("words.access_key")}</KeyLabel>
                                    <KeyValue>{genKey}</KeyValue>
                                    <CopyBtn type="button" onClick={copyKey} $done={copied} aria-label={t("words.copy")}>
                                        {copied ? <RiCheckLine/> : <RiFileCopyLine/>} {copied ? t("words.copied") : t("words.copy")}
                                    </CopyBtn>
                                </KeyValueRow>
                                <KeyNote><RiErrorWarningLine/> {t("words.access_key_copy_note")}</KeyNote>
                            </KeyCard>
                        ) : (
                            <>
                                <KeyBtn type="button" onClick={() => setGenConfirmOpen(true)} disabled={genLoading}>
                                    <RiKey2Line/> {genLoading ? t("words.loading") : t("words.generate_access_key")}
                                </KeyBtn>
                                {genErr ? <KeyNote style={{color: "#EF4444"}}><RiErrorWarningLine/> {t("words.something_wrong")}</KeyNote> : null}
                            </>
                        )
                    ) : null}
                </HInfo>
                {!owner && (
                    <HActions>
                        <MsgBtn href={`mailto:${operator.email}`}><RiSendPlaneFill/> {t("words.message")}</MsgBtn>
                        <RemoveBtn type="button" onClick={() => setConfirmOpen(true)}><RiCloseCircleLine/> {t("words.remove_access")}</RemoveBtn>
                    </HActions>
                )}
            </HeaderCard>

            <Card>
                <CardTitle>{t("words.role")}</CardTitle>
                <Hint>{t("words.role_bundle_hint")}</Hint>
                <RoleGrid>
                    {ROLE_CARDS.map((r) => (
                        <RoleCard key={r} type="button" $active={draft.role === r} $disabled={owner || r === "owner"} onClick={() => pickRole(r)}>
                            <RoleName $role={r}><BsAwardFill/> {t(`words.role_${r}`)}</RoleName>
                            <RoleDesc>{t(`words.role_${r}_desc`)}</RoleDesc>
                        </RoleCard>
                    ))}
                </RoleGrid>
            </Card>

            <Card>
                <CardHead>
                    <CardTitle>{t("words.admin_privileges")}</CardTitle>
                    <PresetBtn type="button" onClick={resetPreset} disabled={owner}><BsAwardFill/> {t("words.operator_preset")}</PresetBtn>
                </CardHead>
                <Hint>{t("words.granular_hint")}</Hint>
                {MATRIX.map((g) => (
                    <Group key={g.group}>
                        <GroupTitle>{t(`words.${g.group}`)}</GroupTitle>
                        {g.perms.map(([key, label]) => (
                            <Row key={key}>
                                <RowInfo>
                                    <RowName>{t(`words.${label}`)}</RowName>
                                    {key === "can_disconnect_number" ? <OwnerOnly>{t("words.owner_only")}</OwnerOnly> : null}
                                </RowInfo>
                                <Toggle checked={!!perms[key]}
                                        disabled={owner || key === "can_disconnect_number"}
                                        onChange={() => toggle(key)} aria-label={t(`words.${label}`)}/>
                            </Row>
                        ))}
                    </Group>
                ))}
            </Card>

            <ConfirmDialog open={confirmOpen} danger
                message={t("words.remove_access_confirm")} confirmLabel={t("words.remove_access")}
                onConfirm={() => { setConfirmOpen(false); onRevoke(); }} onCancel={() => setConfirmOpen(false)}/>

            <ConfirmDialog open={genConfirmOpen}
                title={t("words.generate_access_key")}
                message={t("words.generate_access_key_confirm")}
                confirmLabel={t("words.generate_access_key")}
                onConfirm={() => { setGenConfirmOpen(false); generateKey(); }}
                onCancel={() => setGenConfirmOpen(false)}/>

            {!owner ? (
                <SaveBar>
                    {dirty ? <SaveNote>{t("words.unsaved_changes")}</SaveNote> : <span/>}
                    <SaveActions>
                        <CancelChangesBtn type="button" onClick={onCancelChanges} disabled={!dirty}>{t("words.cancel")}</CancelChangesBtn>
                        <SaveChangesBtn type="button" onClick={onSaveChanges} disabled={!dirty}>{t("words.save")}</SaveChangesBtn>
                    </SaveActions>
                </SaveBar>
            ) : null}
        </Wrap>
    );
};

export default OperatorDetail;
